// Ad Creator hooks (ads/drafts + /ads/creatives + POST /ads/campaigns, /marketing/campaigns/new) — the creative
// session row, the creative upload (SPA → S3-degrade blob → complete → core-worker chunked /advideos for video),
// and the Idem-keyed "Push Live" create. Over the generated client via the tenant-keyed wrappers.
//
// There is no save-and-resume here: the Ad Creator's drafts list went on 2026-09-09 (it had no legacy
// counterpart). `ad_drafts` remains as the row the creative binds to, minted once per upload and deleted when the
// campaign is created.
import { useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { API_URL } from "../../config/env.js";
import { useApp } from "../appContext.js";
import { useBandContext } from "../bandContext.js";
import { useBandQuery, useBandMutation } from "./useBandQuery.js";
import { ApiError } from "./apiError.js";

export type AdDraftView = components["schemas"]["AdDraftView"];
export type CreativeStatusView = components["schemas"]["CreativeStatusView"];
export type CampaignCreateView = components["schemas"]["CampaignCreateView"];
export type CreateCampaignApiRequest = components["schemas"]["CreateCampaignApiRequest"];
export type AdInterestSuggestionsView = components["schemas"]["AdInterestSuggestionsView"];
export type AdInterestSuggestion = components["schemas"]["AdInterestSuggestion"];
export type AdLookalikeSourcesView = components["schemas"]["AdLookalikeSourcesView"];
export type AdLookalikeSourceView = components["schemas"]["AdLookalikeSourceView"];
export type AdAccountAudienceView = components["schemas"]["AdAccountAudienceView"];
export type AdLookalikeStatusView = components["schemas"]["AdLookalikeStatusView"];

/** The whole-form snapshot the Ad Creator round-trips in a draft's `form` (server-opaque except `creative`). */
export interface AdCreativeState {
  mediaId: string;
  kind: "image" | "video";
  status: "uploading" | "ready" | "failed";
  metaVideoId?: string;
  reason?: string;
  filename?: string;
}

export interface AdForm {
  promoteType?: "url" | "release";
  destinationUrl?: string;
  releaseId?: string | null;
  campaignName?: string;
  objective?: string;
  headline?: string;
  primaryText?: string;
  cta?: string;
  countries?: string;
  ageMin?: number;
  ageMax?: number;
  regions?: string;
  cities?: string;
  radiusMiles?: number;
  interestIds?: string[];
  placementMode?: "smart" | "custom";
  platforms?: string[];
  facebookPositions?: string[];
  instagramPositions?: string[];
  budgetMode?: "duration" | "continuous";
  dailyBudgetCents?: number;
  durationDays?: number;
  createPaused?: boolean;
  creative?: AdCreativeState;
}

/**
 * Poll ONE creative's readiness while a video uploads to Meta in core-worker.
 *
 * It replaced `useAdsDrafts`, which listed the band's saved forms — the save-and-resume feature, removed
 * 2026-09-09 (no legacy counterpart). The list happened to answer this question too, by fetching every stored
 * form snapshot on a 2.5s timer and finding its own; this asks the question directly.
 *
 * `draftId` null ⇒ disabled, so a creator with no upload in flight issues nothing at all.
 */
export function useCreativeStatus(draftId: string | null, refetchInterval?: number) {
  const { api } = useApp();
  return useBandQuery<CreativeStatusView>(
    ["ads-creative", draftId ?? "none"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/ads/drafts/{draftId}/creative", {
        params: { path: { bandId, draftId: draftId! } },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    { enabled: draftId != null, refetchInterval },
  );
}

/**
 * The audience builder's suggested interests, seeded server-side from the band's own artist profile.
 *
 * `enabled` is the modal's open state: this spends a vendor call per profile seed, so it must not run for a
 * page that merely MOUNTS the creator. Cached for the session (`staleTime: Infinity`) — a band's comparables do
 * not move while a campaign is being written, and re-suggesting on every refocus would re-spend those calls.
 */
export function useInterestSuggestions(enabled = true) {
  const { api } = useApp();
  return useBandQuery<AdInterestSuggestionsView>(
    ["ads-interest-suggestions"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/ads/interest-suggestions", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    { enabled, staleTime: Infinity },
  );
}

/**
 * The lookalike mirrors (IG + FB fans, IG fans, FB fans) with Meta's sizes and a per-row state, plus the custom
 * audiences already on the band's ad account (ART-295).
 *
 * Fetched only once the lookalike block is switched ON: the first read for a band CREATES the engagement
 * audiences at Meta (they have to exist before Meta can size them), and a band that never uses a lookalike
 * should not get them on its ad account just for opening the builder. Not cached for the session like the
 * interest suggestions — a freshly created audience fills over minutes, so a later look should see it grow.
 */
export function useLookalikeSources(enabled: boolean) {
  const { api } = useApp();
  return useBandQuery<AdLookalikeSourcesView>(
    ["ads-lookalike-sources"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/ads/lookalike-sources", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    { enabled, staleTime: 60_000 },
  );
}

/** What the lookalike would mirror, as the status read and the create take it. */
export interface LookalikeSelection {
  source: string;
  audienceId?: string | null;
  ratioPercent: number;
  countries: string[];
}

/**
 * The build status of the lookalike the current settings would use: `new` (built at Push live), `building`,
 * `ready` or `too-small` (with the countries). One Graph read per lookalike, and none when the band has no
 * lookalike for these settings — so it is keyed on the settings and simply re-asks when they change. No polling:
 * a lookalike takes hours, and the line says the ad can launch while it builds.
 */
export function useLookalikeStatus(selection: LookalikeSelection | null) {
  const { api } = useApp();
  const key = selection
    ? [selection.source, selection.audienceId ?? "", selection.ratioPercent, [...selection.countries].sort().join(",")]
    : ["none"];
  return useBandQuery<AdLookalikeStatusView>(
    ["ads-lookalike-status", ...key.map(String)],
    async (bandId) => {
      const sel = selection!;
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/ads/lookalike-status", {
        params: {
          path: { bandId },
          query: {
            source: sel.source,
            ratioPercent: sel.ratioPercent,
            countries: sel.countries.join(","),
            audienceId: sel.audienceId ?? undefined,
          },
        },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    { enabled: selection != null && selection.countries.length > 0, staleTime: 60_000 },
  );
}

export type SaveDraftVariables = { draftId?: string | null; name?: string | null; form: AdForm };

/**
 * POST (upsert) the creative SESSION row. Returns it; its `draftId` is what the upload binds to.
 *
 * Not a "save" in any sense the user sees — the Ad Creator has no drafts list to save INTO. It is called once,
 * when the first file is picked, because the creative has to hang off something the server owns.
 */
export function useSaveDraft() {
  const { api } = useApp();
  return useBandMutation<AdDraftView, SaveDraftVariables>(
    async (band, { draftId, name, form }) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/ads/drafts", {
        params: { path: { bandId: band } },
        body: { draftId: draftId ?? null, name: name ?? null, form: form as unknown as components["schemas"]["JsonNode"] },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    // No invalidation: nothing READS this row through the cache any more (the list is gone, and the creative
    // poll is keyed on the draft id and fetches on its own interval).
  );
}

/**
 * The ad-creative upload: POST /ads/creatives/upload-url (mint) → PUT the bytes (XHR progress) → POST complete. For a
 * VIDEO, complete enqueues the core-worker chunked /advideos upload — the returned status is `uploading` until the
 * worker finishes (the modal polls the draft). For an IMAGE, complete is `ready` (Meta /adimages runs at create).
 */
export function useUploadAdCreative() {
  const { api, tokenProvider } = useApp();
  const { bandId } = useBandContext();

  async function uploadCreative(
    file: File,
    draftId: string,
    kind: "image" | "video",
    onProgress?: (fraction: number) => void,
    signal?: AbortSignal,
  ): Promise<CreativeStatusView> {
    const contentType = file.type || "application/octet-stream";

    const mint = await api.POST("/api/v1/bands/{bandId}/ads/creatives/upload-url", {
      params: { path: { bandId } },
      body: { draftId, kind, contentType, sizeBytes: file.size, filename: file.name || null },
    });
    if (!mint.response.ok || !mint.data?.data?.mediaId || !mint.data?.data?.uploadUrl) {
      throw new ApiError(mint.response.status, mint.error);
    }
    const { mediaId, uploadUrl, method } = mint.data.data;

    await putBytes(uploadUrl, method || "PUT", file, contentType, tokenProvider.getBearer(), onProgress, signal);

    const done = await api.POST("/api/v1/bands/{bandId}/ads/creatives/{mediaId}/complete", {
      params: { path: { bandId, mediaId } },
      body: { draftId, kind, contentType, filename: file.name || null },
    });
    if (!done.response.ok || !done.data?.data) throw new ApiError(done.response.status, done.error);
    return done.data.data;
  }

  return { uploadCreative };
}

/** POST /ads/campaigns — "Push Live". Idem-keyed (money/publish scope) so a repeat never double-creates; the server
 * tags the release + publishes the ad-campaign event. Returns the provider-guarded status strip + the campaign. */
export function useCreateCampaign() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<CampaignCreateView, CreateCampaignApiRequest>(
    async (band, request) => {
      // Key on the draftId (a UUID) so a double "Push Live" on the SAME draft dedups server-side (the second is a
      // replay), never two Meta campaigns — the draft's form is stable per push, so the body-hash matches.
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/ads/campaigns", {
        params: { path: { bandId: band } },
        headers: { "Idempotency-Key": request.draftId ?? crypto.randomUUID() },
        body: request,
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: ["band", bandId, "ads-campaigns"] });
        void qc.invalidateQueries({ queryKey: ["band", bandId, "ads-insights"] });
        // The session row is DELETED server-side by the create, so its poll must not keep asking about it —
        // the endpoint answers `uploading` for a row that is gone, which would read as an upload restarting.
        void qc.removeQueries({ queryKey: ["band", bandId, "ads-creative"] });
      },
    },
  );
}

function putBytes(
  url: string,
  method: string,
  file: File,
  contentType: string,
  bearer: string | null,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  // The degrade blob endpoint is always same-origin-relative (go-live swaps in an absolute S3 multipart target,
  // PUT bearer-free). XHR (not fetch) so upload.onprogress can drive the bar.
  const sameOrigin = url.startsWith("/") && !url.startsWith("//");
  const target = sameOrigin ? `${API_URL}${url}` : url;
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, target);
    if (sameOrigin && bearer) {
      xhr.setRequestHeader("Authorization", `Bearer ${bearer}`);
      xhr.withCredentials = true;
    }
    xhr.setRequestHeader("Content-Type", contentType);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.total > 0 ? e.loaded / e.total : 0);
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new ApiError(xhr.status, { title: "Upload failed", detail: "The creative couldn't be uploaded." }));
    xhr.onerror = () => reject(new ApiError(0, { title: "Upload failed", detail: "The upload connection failed." }));
    xhr.onabort = () => reject(new ApiError(0, { title: "Upload cancelled", detail: "The upload was cancelled." }));
    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        return;
      }
      signal.addEventListener("abort", () => xhr.abort(), { once: true });
    }
    xhr.send(file);
  });
}
